import { useContext, useState } from 'react';
import Modal from 'react-modal';
import { GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';
import Image from 'next/legacy/image';

import { RecipeType } from '@nextTypes/recipe';

import { Footer } from '@components/Footer/Footer';
import Meta from '@components/Meta';
import { NavBar } from '@components/NavBar/NavBar';

import { EmailCollectionProps } from '@components/Recipes/EmailCollection';

import { H2, H3 } from '@common/headline';
import { BodyRegular } from '@common/body';
import colours from '@common/colours';
import { NavigationButton } from '@common/buttons';
import { sanitiseCookingTimes } from '@nextUtils/recipeUtils';
import { DownloadAppBanner } from '@components/DownloadAppBanner/DownloadAppBanner';
import { MainContext } from '@components/context/MainProvider';
import { getAPIUrl } from '@nextUtils/urlUtils';

import freezerIcon from '@public/img/icons/snowflake.png';
import cancelCross from '@public/img/icons/cancel-cross.png';
import thumbOutline from '@public/img/icons/thumbOutline.png';

// dynamically importing so we can chose not to render when the cookie
// for hiding it is set and we get no ssr errors
const DynamicEmailCollection = dynamic<EmailCollectionProps>(
  () =>
    import('@components/Recipes/EmailCollection').then(
      mod => mod.EmailCollection,
    ),
  { ssr: false },
);

type Props = {
  recipe?: RecipeType;
};

const modalStyle = {
  content: {
    width: '100%',
    left: '50%',
    right: '50%',
    transform: 'translate(-50%, -50%)',
    bottom: 'initial',
    top: '50%',
    padding: '0 42px 32px',
    overflow: 'auto',
    border: 'none',
    height: 'auto',
    maxHeight: '100%',
  },
  overlay: {
    backgroundColor: `${colours.snBlack}`,
    zIndex: 9999,
  },
};

export const getServerSideProps: GetServerSideProps = async ({ query }) => {
  const { recipeId } = query;

  const apiUrl = getAPIUrl();

  const response = await fetch(`${apiUrl}/api/recipes/${recipeId}`);
  if (response.ok) {
    const recipe = await response.json();
    return {
      props: {
        recipe,
      },
    };
  }

  return { props: {} };
};

const Recipe = ({ recipe }: Props): JSX.Element => {
  const { user } = useContext(MainContext);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => setIsModalOpen(true);
  const onRequestClose = () => setIsModalOpen(false);
  const isEmailCollectionHidden = true;
  // Hide download app banner for logged in users
  const showDownloadBanner = user === null;

  if (!recipe || !recipe.title) {
    return (
      <div className="m-auto mt-10 max-w-[1000px]">
        <H3>Recipe not found</H3>
      </div>
    );
  }

  return (
    <>
      <Meta
        title={`${recipe.title} recipe on Second Nature`}
        noFollow
        ogImageUrl={recipe.imageUrl}
        customProperties={{ hideIntercom: true }}
      />
      <NavBar />
      <Modal
        isOpen={isModalOpen}
        onRequestClose={onRequestClose}
        style={modalStyle}
        contentLabel="Recipe video"
        closeTimeoutMS={150}
      >
        <a onClick={onRequestClose} className="pull-right my-4 cursor-pointer">
          <Image src={cancelCross} alt="Cancel" width={40} height={40} />
        </a>
        <video controls autoPlay width="100%">
          <source src={recipe.videoUrl} />
        </video>
      </Modal>
      <div className="mb-20 mt-16">
        {showDownloadBanner && (
          <div className="h-[94px]">
            <DownloadAppBanner trackingId="Clicked download app recipe" />
          </div>
        )}
        {!isEmailCollectionHidden && (
          <DynamicEmailCollection recipeId={recipe._id} />
        )}
        <div className="m-auto max-w-[1000px] px-8">
          <div className="flex flex-col pt-16 md:flex-row">
            <div className="w-full max-w-[500px] self-center md:max-w-[300px] md:self-auto">
              <img src={recipe.imageUrl} />
            </div>
            <div className="mt-10 text-blue md:mt-0 md:pl-10">
              <H2>{recipe.title}</H2>
              <div className="mb-8 mt-6 flex shrink-0 flex-col gap-4 rounded bg-blue-50 px-4 py-5 text-blue">
                <BodyRegular>
                  <strong>Prep and cooking time: </strong>
                  {sanitiseCookingTimes(
                    recipe.minCookingTime,
                    recipe.maxCookingTime,
                  )}
                </BodyRegular>
                <BodyRegular>
                  <strong>Complex carb portions, per serving: </strong>
                  {recipe.carbohydrates}
                </BodyRegular>
              </div>
              {recipe.categories.includes('freezable') && (
                <div className="my-8 flex items-center gap-2 text-blue">
                  <Image src={freezerIcon} width={20} height={20} />
                  <BodyRegular>Freezable</BodyRegular>
                </div>
              )}
              {recipe.videoUrl && (
                <div className="pb-4 md:pb-0">
                  <NavigationButton
                    width="200px"
                    borderRadius={30}
                    onClick={openModal}
                  >
                    Play video
                  </NavigationButton>
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col pt-4 md:flex-row md:pt-16">
            <div className="shrink-0 md:w-[300px]">
              <div className="mb-6 flex justify-between text-blue">
                <BodyRegular>
                  <strong>Ingredients</strong>
                </BodyRegular>
                {recipe.servings && (
                  <BodyRegular>
                    <strong>Serves: </strong>
                    <span>{recipe.servings}</span>
                  </BodyRegular>
                )}
                {recipe.makes && (
                  <BodyRegular>
                    <strong>Makes: </strong>
                    <span>
                      {recipe.makes.amount}
                      {recipe.makes.unit}
                    </span>
                  </BodyRegular>
                )}
              </div>
              {recipe.ingredients.map(
                ({ ingredient, amount, orIngredient, orAmount }) => (
                  <div
                    className="mb-3 border-b-[1px] border-blue-200 pb-3"
                    key={amount + ingredient}
                  >
                    <BodyRegular>
                      <strong>{amount} </strong>
                      {ingredient}
                      {orIngredient && (
                        <>
                          <em> or </em>
                          {orAmount && <strong>{orAmount} </strong>}
                          {orIngredient}
                        </>
                      )}
                    </BodyRegular>
                  </div>
                ),
              )}
            </div>
            <div className="mt-10 text-blue md:mt-0 md:pl-10">
              <BodyRegular>
                <strong>Method</strong>
              </BodyRegular>
              <ol className="mt-6">
                {recipe.method.map(methodStep => (
                  <li key={methodStep} className="mb-6">
                    <BodyRegular>{methodStep}</BodyRegular>
                  </li>
                ))}
              </ol>
              {recipe.specialTip && (
                <div className="mt-10 flex p-4 shadow-lg">
                  <div className="mr-2">
                    <Image src={thumbOutline} width={35} height={35} />
                  </div>
                  <div className="text-blue">
                    <BodyRegular>
                      <strong>Second Nature special tip</strong>
                    </BodyRegular>
                    <BodyRegular>{recipe.specialTip}</BodyRegular>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

Modal.setAppElement('body');

export default Recipe;
